import React, { useState, useCallback, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Checkbox from "aldoo-ra/Checkbox"
import Badge from "aldoo-ra/Badge"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { api, generateCRUDInterface } from "aldoo-ra/API"
import { url } from "aldoo-ra/CMS/url"
import { GetUserDevices } from "aldoo-ra/Admin/ProductLicensing/api"
import { getPlatformIcon } from "aldoo-ra/Admin/ProductLicensing/PlatformIcons"
import Tooltip from "aldoo-ra/Tooltip"
import UserPlatformAPI from "./user-api"
import UserInfoDrawer from "./UserInfoDrawer"
import { renderAvatar } from "./render-avatar"
import moment from "moment"
import {
  UsersIcon,
  PlusCircleIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DevicePhoneMobileIcon,
  CreditCardIcon,
  KeyIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"

// Create User Modal component
const CreateUserModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    status: 'active'
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Initialize CRUD interface for User model
  const crudAPI = api("crud")
  const userCRUD = generateCRUDInterface(crudAPI, "User")

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required"
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address"
    }


    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      setLoading(true)

      const userData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        status: formData.status
      }

      // Check if user with this email already exists
      const emailCheckResponse = await UserPlatformAPI.CheckEmailExists(userData.email)

      if (!emailCheckResponse.result) {
        showNotification({
          message: emailCheckResponse.error || "Failed to validate email",
          className: "bg-admin_error text-white",
          duration: 4000
        })
        return
      }

      if (emailCheckResponse.exists) {
        setErrors({ email: "A user with this email address already exists" })
        return
      }

      const response = await UserPlatformAPI.CreateUser(userData)

      if (response.result) {
        showNotification({
          message: `User "${userData.name}" created successfully`,
          className: "bg-admin_success text-white",
          duration: 3000
        })

        // Reset form
        setFormData({ name: '', email: '', status: 'active' })
        setErrors({})
        onSuccess && onSuccess(response.data)
        onClose()
      } else {
        showNotification({
          message: response.error || "Failed to create user",
          className: "bg-admin_error text-white",
          duration: 4000
        })
      }
    } catch (error) {
      showNotification({
        message: error.message || "Failed to create user",
        className: "bg-admin_error text-white",
        duration: 4000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({ name: '', email: '', status: 'active' })
    setErrors({})
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New User"
      variant="admin_centered"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Typography variant="admin_txt1_dim">
            Full Name
          </Typography>

          <Input
            variant="admin"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter full name"
            disabled={loading}
            error={errors.name}
          />
        </div>

        <div>
          <Typography variant="admin_txt1_dim">
            Email Address
          </Typography>

          <Input
            variant="admin"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="Enter email address"
            disabled={loading}
            error={errors.email}
          />
        </div>


        <div>
          <Typography variant="admin_txt1_dim">
            Status
          </Typography>

          <DropdownSelect
            variant="admin"
            value={[{ value: formData.status, name: formData.status.charAt(0).toUpperCase() + formData.status.slice(1) }]}
            onChange={(selected) => {
              if (selected && selected.length > 0) {
                handleChange('status', selected[0].value)
              }
            }}
            options={[
              { value: 'active', name: 'Active' },
              { value: 'inactive', name: 'Inactive' },
              { value: 'suspended', name: 'Suspended' }
            ]}
            multiSelection={false}
            className="w-full"
            disabled={loading}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="admin_secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="admin_primary"
            loading={loading}
          >
            Create User
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// User credentials display component
const UserCredentials = ({ user, onViewCredentials }) => {
  const credentials = [
    { key: "Google ID", value: user.google_id },
    { key: "Apple ID", value: user.apple_id },
    { key: "Facebook ID", value: user.facebook_id },
    { key: "Internal ID", value: user.internal_id },
    { key: "Stripe Customer", value: user.stripe_customer_id },
  ].filter(cred => cred.value)

  if (credentials.length === 0) {
    return <span className="text-admin_text_secondary text-sm">None</span>
  }

  return (
    <button
      className="flex items-center gap-1 text-admin_primary hover:text-admin_primary/80 whitespace-nowrap transition-colors"
      onClick={() => onViewCredentials(user)}
    >
      <KeyIcon className="hidden xl:block w-4 h-4" />
      {credentials.length} credential{credentials.length > 1 ? 's' : ''}
    </button>
  )
}

// User credentials modal
const UserCredentialsModal = ({ user, isOpen, onClose }) => {
  const [copiedStates, setCopiedStates] = useState({})

  if (!user) return null

  const credentials = [
    { key: "Email", value: user.email },
    { key: "Google ID", value: user.google_id },
    { key: "Apple ID", value: user.apple_id },
    { key: "Facebook ID", value: user.facebook_id },
    { key: "Internal ID", value: user.internal_id },
    { key: "Stripe Customer ID", value: user.stripe_customer_id },
  ].filter(cred => cred.value)

  const handleCopy = async (value, index, credentialName) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedStates(prev => ({ ...prev, [index]: true }))

      // Show success notification
      showNotification({
        message: `${credentialName} copied to clipboard`,
        className: "bg-admin_primary text-admin_text_inv"
      })

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [index]: false }))
      }, 2000)
    } catch (error) {
      console.error("Failed to copy text:", error)
      showNotification({
        message: "Failed to copy to clipboard",
        className: "bg-admin_error text-white"
      })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_centered"
      title={`User Credentials`}
      className="max-w-3xl"
    >
      <div className="space-y-6">
        {/* User info header */}
        <div className="flex items-center gap-3 bg-admin_primary/10 rounded-lg border border-admin_bordered p-4">
          <div className="w-12 h-12 flex items-center justify-center bg-admin_primary rounded-full flex-shrink-0">
            <Typography 
              variant="admin_h4" 
              className="text-white"
            >
              {(user.name || user.email || "U").charAt(0).toUpperCase()}
            </Typography>
          </div>

          <div>
            <Typography variant="admin_h4" className="font-medium">
              {user.name || "Unknown User"}
            </Typography>

            <Typography 
              variant="admin_txt1" 
              className="text-admin_text_secondary"
            >
              {user.email}
            </Typography>
          </div>
        </div>

        {/* Credentials grid */}
        {credentials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {credentials.map((cred, index) => (
              <div key={index} className="border border-admin_bordered rounded-lg hover:border-admin_primary/30 p-4  transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-admin_primary/10 rounded-full">
                    <KeyIcon className="w-4 h-4 text-admin_primary" />
                  </div>

                  <Typography variant="admin_label" className="my-0">
                    {cred.key}
                  </Typography>
                </div>
                
                <div className="bg-admin_canvas rounded border border-admin_bordered/50 p-3 group">
                  <div className="flex items-center justify-between">
                    <Typography 
                      variant="admin_txt1_dim" 
                      className="font-mono break-all flex-1 pr-3"
                    >
                      {cred.value}
                    </Typography>

                    <Button
                      variant="admin_icon"
                      onClick={() => handleCopy(cred.value, index, cred.key)}
                    >
                      {copiedStates[index] ? (
                        <CheckIcon className="w-4 h-4 text-admin_success" />
                      ) : (
                        <Tooltip content="Copy to clipboard">
                          <ClipboardDocumentIcon className="w-4 h-4 text-admin_text_secondary hover:text-admin_primary" />
                        </Tooltip>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-admin_canvas flex items-center justify-center mx-auto mb-4">
              <KeyIcon className="w-8 h-8 text-admin_text_secondary" />
            </div>

            <Typography 
              variant="admin_h4" 
              className="text-admin_text_secondary mb-2"
            >
              No Credentials Found
            </Typography>

            <Typography variant="admin_txt1_dim">
              This user has no authentication credentials configured
            </Typography>
          </div>
        )}
      </div>
    </Modal>
  )
}

// Devices display component
const UserDevices = ({ user, onViewDevices }) => {
  const deviceCount = user.devices?.length || 0

  return (
    <button
      className="flex items-center gap-1 text-admin_primary hover:text-admin_primary/80 whitespace-nowrap"
      onClick={() => onViewDevices(user)}
      disabled={deviceCount === 0}
    >
      <DevicePhoneMobileIcon className="hidden xl:block w-4 h-4" />
      {deviceCount} device{deviceCount !== 1 ? 's' : ''}
    </button>
  )
}

// User details modal
const UserDetailsModal = ({ user, isOpen, onClose, onUpdate, onDelete, onToggleStatus }) => {
  const [editingField, setEditingField] = useState(null)
  const [editValues, setEditValues] = useState({})
  const [storeAccount, setStoreAccount] = useState(null)
  const [loadingStoreAccount, setLoadingStoreAccount] = useState(false)

  // Fetch store account when modal opens
  React.useEffect(() => {
    if (isOpen && user?._id) {
      setLoadingStoreAccount(true)
      UserPlatformAPI.GetUserStoreAccount(user._id, user.email)
        .then(response => {
          if (response.result) {
            setStoreAccount(response.data)
          }
        })
        .catch(error => {
          console.error("Failed to load store account:", error)
        })
        .finally(() => {
          setLoadingStoreAccount(false)
        })
    } else {
      setStoreAccount(null)
    }
  }, [isOpen, user?._id, user?.email])

  if (!user) return null

  const handleEditStart = (field, currentValue) => {
    setEditingField(field)
    setEditValues({ ...editValues, [field]: currentValue || '' })
  }

  const handleEditSave = async (field) => {
    try {
      const updateData = { [field]: editValues[field] }
      if (field === 'created') {
        updateData[field] = new Date(editValues[field]).toISOString()
      }

      await onUpdate(user._id, updateData)
      setEditingField(null)
      showNotification({
        message: `${field} updated successfully`,
        className: "bg-admin_primary text-admin_text_inv"
      })
    } catch (error) {
      console.error(`Failed to update ${field}:`, error)
      showNotification({
        message: `Failed to update ${field}`,
        className: "bg-admin_error text-white"
      })
    }
  }

  const handleEditCancel = () => {
    setEditingField(null)
    setEditValues({})
  }

  const handleStatusChange = async (newStatus) => {
    try {
      await onToggleStatus(user, newStatus === 'true')
      showNotification({
        message: `User ${newStatus === 'true' ? 'enabled' : 'disabled'} successfully`,
        className: "bg-admin_primary text-admin_text_inv"
      })
    } catch (error) {
      console.error("Failed to update user status:", error)
      showNotification({
        message: "Failed to update user status",
        className: "bg-admin_error text-white"
      })
    }
  }

  const EditableField = ({ label, field, value, type = "text" }) => {
    const isEditing = editingField === field

    return (
      <div className="flex justify-between items-center py-2 px-3 bg-admin_canvas rounded">
        <Typography variant="admin_txt1_dim">
          {label}
        </Typography>

        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <input
                type={type}
                value={editValues[field] || ''}
                onChange={(e) => setEditValues({ ...editValues, [field]: e.target.value })}
                className="min-w-[200px] font-mono text-sm bg-admin_paper border border-admin_bordered rounded px-2 py-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleEditSave(field)
                  if (e.key === 'Escape') handleEditCancel()
                }}
                autoFocus
              />

              <button
                onClick={() => handleEditSave(field)}
                className="text-admin_success hover:text-admin_success/80 p-1"
                title="Save"
              >
                <CheckIcon className="w-4 h-4" />
              </button>

              <button
                onClick={handleEditCancel}
                className="text-admin_text_secondary hover:text-admin_text p-1"
                title="Cancel"
              >
                ×
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Typography 
                variant="admin_txt1"
                className="font-mono"
              >
                {value || 'Not set'}
              </Typography>

              <button
                onClick={() => handleEditStart(field, value)}
                className="text-admin_text_secondary hover:text-admin_primary opacity-0 group-hover:opacity-100 transition-opacity p-1"
                title={`Edit ${label}`}
              >
                <PencilIcon className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_centered"
      title={`User Details - ${user.name}`}
      className="w-full md:max-w-[90%] xl:max-w-[1200px]"
    >
      <div className="space-y-6">
        {/* User Info */}
        <div className="flex items-start gap-4">
          {/* Avatar */}
          {renderAvatar(user, "w-16 h-16", "text-2xl")}

          <div className="flex-1">
            <Typography 
              component="h2"
              variant="admin_h3" 
              className="mb-1"
            >
              {user.name}
            </Typography>

            {user.email && (
              <Typography 
                variant="admin_body" 
                className="text-admin_text_secondary mt-0 mb-2"
              >
                {user.email}
              </Typography>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
              {user.stripe_customer_id && (
                <Badge 
                  variant="admin_info" 
                  className="flex items-center gap-1"
                >
                  <CreditCardIcon className="w-3 h-3" />
                  Stripe Customer
                </Badge>
              )}
              
              <DropdownSelect
                variant="admin"
                options={[
                  { value: 'true', name: 'Active' },
                  { value: 'false', name: 'Disabled' }
                ]}
                value={user.enabled
                  ? [{ value: 'true', name: 'Active' }]
                  : [{ value: 'false', name: 'Disabled' }]
                }
                onChange={(values) => {
                  const selectedValue = values?.[0]?.value || 'false'
                  handleStatusChange(selectedValue)
                }}
                showCheckbox={false}
                fixedWidth="120px"
              />
            </div>
        </div>

        {/* Details Grid */}
        <div className="w-full grid grid-cols-2 gap-4">
          <div className="w-full border border-admin_bordered rounded-xl p-4">
            <Typography 
              variant="admin_label" 
              className="mb-1"
            >
              Devices:
            </Typography>

            <Typography 
              variant="admin_body_dim" 
              className="my-0"
            >
              {user.devices?.length || 0} connected
            </Typography>
          </div>

          <div className="w-full border border-admin_bordered rounded-xl p-4">
            <Typography 
              variant="admin_label" 
              className="mb-1"
            >
              Account Type:
            </Typography>

            {loadingStoreAccount ? (
              <Typography 
                variant="admin_body_dim" 
                className="my-0"
              >
                Loading...
              </Typography>

            ) : storeAccount ? (
              <Typography 
                variant="admin_body_dim" 
                className="my-0"
              >
                {storeAccount.account_type || 'Not specified'}
              </Typography>
            ) : (
              <Typography 
                variant="admin_body_dim" 
                className="my-0"
              >
                No store account
              </Typography>
            )}
          </div>
        </div>

        {/* Store Account Details */}
        {storeAccount && (
          <div>
            <Typography 
              variant="admin_txt1" 
              className="font-medium mb-3"
            >
              Store Account
            </Typography>

            <div className="grid grid-cols-1 gap-2">
              {[
                { label: "Account Type", value: storeAccount.account_type },
                { label: "Account ID", value: storeAccount._id },
                { label: "State", value: storeAccount.state },
                { label: "Status", value: storeAccount.status },
                { label: "Created", value: storeAccount.created ? new Date(storeAccount.created).toLocaleDateString() : null },
              ].map((item, index) => (
                item.value && (
                  <div key={index} className="flex justify-between items-center bg-admin_canvas rounded py-2 px-3">
                    <Typography variant="admin_txt1_dim">
                      {item.label}
                    </Typography>

                    <Typography 
                      variant="admin_txt1"
                      className="font-mono"
                    >
                      {item.value}
                    </Typography >
                  </div>
                )
              ))}
            </div>

            {/* Products/Subscriptions */}
            {storeAccount.subscriptions && storeAccount.subscriptions.length > 0 && (
              <div className="mt-4">
                <Typography 
                  variant="admin_label" 
                  className="mb-1"
                >
                  Products Purchased
                </Typography>

                <div className="grid grid-cols-1 gap-2">
                  {storeAccount.subscriptions
                    .filter(sub => !sub.deleted)
                    .map((subscription, index) => (
                      <div key={index} className="bg-admin_canvas rounded border p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <Typography 
                              variant="admin_label" 
                              className="my-0"
                            >
                              PID: {subscription.pid}
                            </Typography>

                            {subscription.product?.name && (
                              <Typography variant="admin_txt1_dim">
                                {subscription.product.name}
                              </Typography>
                            )}
                          </div>

                          <Badge variant={subscription.status === 'active' ? 'admin_success' : 'admin_error'}>
                            {subscription.status || 'unknown'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-admin_text_secondary">
                              Platform: 
                            </span>

                            <span className="font-mono">
                              {subscription.platform || 'N/A'}
                            </span>
                          </div>

                          <div>
                            <span className="text-admin_text_secondary">
                              Duration: 
                            </span>

                            <span className="font-mono">
                              {subscription.duration ? `${subscription.duration} days` : 'N/A'}
                            </span>
                          </div>

                          {subscription.start_date && (
                            <div>
                              <span className="text-admin_text_secondary">
                                Start: 
                              </span>

                              <span className="font-mono">
                                {new Date(subscription.start_date).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                          {subscription.end_date && (
                            <div>
                              <span className="text-admin_text_secondary">
                                End: 
                              </span>

                              <span className="font-mono">
                                {new Date(subscription.end_date).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Editable Fields */}
        <div>
          <Typography 
            component="h3"
            variant="admin_label" 
            className="mb-3"
          >
            User Information
          </Typography>

          <div className="grid grid-cols-1 gap-2 group">
            <EditableField label="Name" field="name" value={user.name} />

            <EditableField label="Email" field="email" value={user.email} type="email" />

            <EditableField label="Google ID" field="google_id" value={user.google_id} />

            <EditableField label="Apple ID" field="apple_id" value={user.apple_id} />

            <EditableField label="Facebook ID" field="facebook_id" value={user.facebook_id} />

            <EditableField label="Stripe Customer ID" field="stripe_customer_id" value={user.stripe_customer_id} />

            <EditableField
              label="Created"
              field="created"
              value={user.created ? new Date(user.created).toISOString().split('T')[0] : ''}
              type="date"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end items-center pt-4 border-t border-admin_bordered">
          <Button variant="admin_error" onClick={() => onDelete(user)}>
            <TrashIcon className="w-4 h-4 mr-1" />
            Delete User
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// Devices modal
const DevicesModal = ({ user, isOpen, onClose }) => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [platformFilter, setPlatformFilter] = useState([{ value: "all", label: "All Platforms" }])

  // Initialize CRUD API
  const crudAPI = api("crud")

  // Refresh the table when dialog opens
  useEffect(() => {
    if (!isOpen) return
    setRefreshKey((prev) => prev + 1)
  }, [isOpen])

  // Refresh when platform filter changes
  useEffect(() => {
    setRefreshKey((prev) => prev + 1)
  }, [platformFilter])

  if (!user) return null

  // API method to get devices for this user
  const getUserDevices = async (requestFields = {}) => {
    try {
      // Get the device IDs from the user object and deduplicate
      const allDeviceIds = user.devices || []
      const deviceIds = [...new Set(allDeviceIds)] // Remove duplicates
      
      if (deviceIds.length === 0) {
        return {
          result: true,
          data: [],
          totalItems: 0
        }
      }

      // Build platform filter
      const platformMatch = {}
      if (platformFilter.length > 0 && !platformFilter.some(f => f.value === "all")) {
        platformMatch.platform = { $in: platformFilter.map(f => f.value) }
      }

      // Query devices using the CRUD API - try multiple ID fields
      const fields = {
        ...requestFields,
        model: "UserDevice",
        match: { 
          $or: [
            { _id: { $in: deviceIds } },
            { deviceId: { $in: deviceIds } },
            { deviceID: { $in: deviceIds } }
          ],
          ...platformMatch,
          ...requestFields.match
        },
        sort: { created: -1 },
      }

      return await crudAPI({ get: fields })
    } catch (error) {
      console.error("Failed to fetch user devices:", error)
      return {
        result: false,
        data: [],
        totalItems: 0
      }
    }
  }

  const formatDate = (date) => {
    if (!date) return "Never"
    try {
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return "Invalid date"
    }
  }

  const getTimeAgo = (date) => {
    if (!date) return "Never"
    try {
      return moment(date).fromNow()
    } catch (error) {
      return "Invalid date"
    }
  }

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return "Unknown Model"
    
    // Check if it's a user agent string (contains Mozilla)
    if (!userAgent.includes("Mozilla")) {
      return userAgent // Return as-is if it's not a user agent
    }

    try {
      let browser = "Unknown Browser"
      let os = "Unknown OS"

      // Extract browser
      if (userAgent.includes("Chrome")) {
        browser = "Chrome"
      } else if (userAgent.includes("Firefox")) {
        browser = "Firefox"
      } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
        browser = "Safari"
      } else if (userAgent.includes("Edge")) {
        browser = "Edge"
      } else if (userAgent.includes("Mozilla")) {
        browser = "Mozilla"
      }

      // Extract OS
      if (userAgent.includes("Macintosh") || userAgent.includes("Mac OS X")) {
        os = "macOS"
      } else if (userAgent.includes("Windows")) {
        os = "Windows"
      } else if (userAgent.includes("Linux")) {
        os = "Linux"
      } else if (userAgent.includes("Android")) {
        os = "Android"
      } else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
        os = "iOS"
      }

      return `${browser} • ${os}`
    } catch (error) {
      return userAgent
    }
  }

  const getStatusBadge = (device) => {
    if (!device.enabled) return <Badge variant="admin_error">Disabled</Badge>
    if (device.online) return <Badge variant="admin_success">Online</Badge>
    if (device.login) return <Badge variant="admin_info">Logged In</Badge>
    return <Badge variant="admin_warning">Offline</Badge>
  }


  // Define columns for the devices table
  const tableItemRender = [
    {
      id: "device",
      title: "Device",
      headerClassName: "w-[300px] xl:w-1/3",
      render: (_, device) => (
        <div className="flex items-center space-x-3">
          {getPlatformIcon(device.platform, "w-8 h-8 text-admin_text_secondary")}

          <div className="w-[200px] xl:w-[360px]">
            <Typography 
              variant="admin_label" 
              className="truncate my-0"
            >
              {device.name || device.deviceName || "Unknown Device"}
            </Typography>

            <div className="w-full flex items-center gap-2 flex-nowrap whitespace-nowrap">
              <Tooltip display="block" content={device.model || "Unknown Model"}>
                <Typography 
                  variant="admin_txt1_dim" 
                  className="whitespace-nowrap"
                >
                  {parseUserAgent(device.model)}
                </Typography>
              </Tooltip>

              <span className="text-admin_text_secondary">•</span>

              <Typography 
                variant="admin_txt1_dim" 
                className="whitespace-nowrap"
              >
                {device.platform || "Unknown"}
              </Typography>

              {device.deviceID && (
                <>
                  <span className="text-admin_text_secondary">•</span>

                  <Tooltip display="block" content={
                    <div className="text-center">
                      <Typography 
                        variant="admin_txt1"
                        className="block"
                      >
                        {device.deviceID}
                      </Typography>

                      <Typography variant="admin_txt2_dim">
                        Click to copy
                      </Typography>
                    </div>
                  }>
                    <button 
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(device.deviceID)
                          showNotification({
                            message: "Copied to clipboard",
                            className: "bg-admin_primary text-admin_text_inv"
                          })
                        } catch (error) {
                          console.error("Failed to copy:", error)
                        }
                      }}
                      className="max-w-[70px] xl:max-w-[170px] font-mono truncate whitespace-nowrap text-admin_text_secondary hover:text-admin_primary transition-colors text-left"
                    >
                      {device.deviceID}
                    </button>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[80px]",
      render: (_, device) => getStatusBadge(device),
    },
    {
      id: "ip",
      title: "IP",
      headerClassName: "w-1/5",
      render: (_, device) => (
        device.ip ? (
          <Tooltip display="block" content={
            <div className="text-center">
              <Typography 
                variant="admin_txt1"
                className="block"
              >
                {device.ip}
              </Typography>

              <Typography variant="admin_txt2_dim">
                Click to copy
              </Typography>
            </div>
          }>
            <button 
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(device.ip)
                  showNotification({
                    message: "Copied to clipboard",
                    className: "bg-admin_primary text-admin_text_inv"
                  })
                } catch (error) {
                  console.error("Failed to copy:", error)
                }
              }}
              className="w-full font-mono truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
            >
              {device.ip}
            </button>
          </Tooltip>
        ) : (
          <Typography 
            variant="admin_txt1" 
            className="text-admin_text font-mono"
          >
            N/A
          </Typography>
        )
      ),
    },
    {
      id: "lastActivity",
      title: "Last Activity",
      headerClassName: "w-[200px]",
      render: (_, device) => (
        <div className="text-sm">
          <div className="flex gap-1 flex-nowrap mb-1">
            <span className="text-admin_text_secondary">
              Login: 
            </span>

            <Tooltip content={getTimeAgo(device.lastLogin)}>
              <span className="text-admin_text whitespace-nowrap cursor-help">
                {formatDate(device.lastLogin)}
              </span>
            </Tooltip>
          </div>

          <div className="flex gap-1 flex-nowrap">
            <span className="text-admin_text_secondary">
              Ping: 
            </span>

            <Tooltip content={getTimeAgo(device.lastPing)}>
              <span className="text-admin_text cursor-help">
                {formatDate(device.lastPing)}
              </span>
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      id: "created",
      title: "Created",
      headerClassName: "w-[170px]",
      render: (_, device) => (
        <Tooltip content={getTimeAgo(device.created)}>
          <Typography 
            variant="admin_txt1_dim" 
            className="whitespace-nowrap cursor-help"
          >
            {formatDate(device.created)}
          </Typography>
        </Tooltip>
      ),
    },
  ]

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      className="md:absolute w-full md:w-[95%] h-full md:h-[100vh] top-[15px]"
      zIndex={200}
    >
      <div className="p-4">
        <Typography 
          component="h1"
          variant="admin_h3" 
          className="mb-4"
        >
          Devices - {user.name}
        </Typography>

        {/* Platform filter */}
        <div className="mb-4">
          <div className="flex items-end gap-4">
            <div className="flex flex-col">
              <Typography 
                variant="admin_txt2_dim" 
                className="mb-0.5"
              >
                Platform
              </Typography>

              <DropdownSelect
                variant="admin"
                options={[
                  { value: "all", label: "All Platforms" },
                  { value: "iOS", label: "iOS" },
                  { value: "Android", label: "Android" },
                  { value: "Windows", label: "Windows" },
                  { value: "Mac", label: "Mac" },
                  { value: "Linux", label: "Linux" },
                  { value: "Web", label: "Web" },
                ]}
                value={platformFilter}
                onChange={(values) => setPlatformFilter(values?.length ? values : [{ value: "all", label: "All Platforms" }])}
                fixedWidth="150px"
                showCheckbox={false}
                searchEnabled={false}
              />
            </div>
          </div>
        </div>

        {/* Data table for displaying devices */}
        <div className="h-[70vh]">
          <DataTable
            variant="admin"
            refreshKey={refreshKey}
            apiMethod={getUserDevices}
            preferredHeight="70vh"
            itemHeight="h-[70px]"
            localUserPreferencesKey="admin.platformAccess.users"
        styleOverrides={{
              table: "w-full table-fixed",
              searchInput: "absolute top-[45px] right-[40px] w-full max-w-[300px] xl:max-w-[360px] ml-6 xl:ml-8"
            }}
            searchField="name platform model deviceID ip"
            searchObject={{}}
            sort={{ created: -1 }}
            renders={tableItemRender}
            noData={
              <div className="min-h-[400px] flex items-center justify-center">
                <div className="text-center">
                  <DevicePhoneMobileIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-3" />
                  
                  <Typography variant="admin_body_dim">
                    No devices connected
                  </Typography>
                </div>
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  )
}

// Confirmation modal for delete actions
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, userCount, isBulk = false }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_centered"
      title="Confirm Deletion"
      className="max-w-md"
    >
      <div className="space-y-4">
        <Typography variant="admin_body">
          {isBulk
            ? `Are you sure you want to delete ${userCount} selected user${userCount !== 1 ? 's' : ''}?`
            : `Are you sure you want to delete this user?`
          }
        </Typography>
        
        <Typography variant="admin_txt1_dim">
          This action will soft-delete the user{userCount !== 1 ? 's' : ''} and can be undone using the restore function.
        </Typography>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="admin_secondary" onClick={onClose}>
            Cancel
          </Button>

          <Button variant="admin_error" onClick={onConfirm}>
            <TrashIcon className="w-4 h-4 mr-1" />
            Delete {isBulk ? `${userCount} User${userCount !== 1 ? 's' : ''}` : 'User'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// Restore deleted users modal
const RestoreUsersModal = ({ isOpen, onClose, onRestore }) => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [deletedUsers, setDeletedUsers] = useState([])

  // Refresh the table when dialog opens
  useEffect(() => {
    if (!isOpen) return
    setRefreshKey((prev) => prev + 1)
  }, [isOpen])

  // Handler for deleting a user forever
  const deleteForeverAction = (user) => {
    showPromptDialog({
      title: "Delete Forever",
      message: `Are you sure you want to permanently delete user "${user.name}" (${user.email?.split('_deleted_')[0] || user.email})? This action cannot be undone.`,
      confirmText: "Delete Forever",
      onConfirm: async () => {
        try {
          await UserPlatformAPI.HardDeleteUsers([user._id])
          showNotification({
            message: "User permanently deleted successfully.",
            className: "bg-admin_primary text-white",
          })
          // Force refresh the restore dialog table
          setTimeout(() => {
            setRefreshKey((prev) => prev + 1)
          }, 50)
          onRestore?.() // Refresh the main table
        } catch (error) {
          showNotification({
            message: "Failed to delete the user.",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handler for restoring a user
  const restoreAction = async (user) => {
    try {
      await UserPlatformAPI.RestoreUser(user._id)
      showNotification({
        message: "Successfully restored the user.",
        className: "bg-admin_primary text-admin_text_inv",
      })
      // Force refresh the restore dialog table
      setTimeout(() => {
        setRefreshKey((prev) => prev + 1)
      }, 50)
      onRestore?.() // Refresh the main table
    } catch (error) {
      showNotification({
        message: "Failed to restore the user.",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handler for deleting all users forever
  const deleteAllAction = async () => {
    try {
      // Fetch ALL deleted users in batches (no limit)
      let allDeletedUsers = []
      let skip = 0
      const batchSize = 1000
      let hasMore = true

      while (hasMore) {
        const response = await UserPlatformAPI.GetDeletedUsers({
          page: Math.floor(skip / batchSize) + 1,
          itemsPerPage: batchSize,
          search: {},
          sort: { created: -1 }
        })

        const users = response?.data || []

        if (Array.isArray(users) && users.length > 0) {
          allDeletedUsers = allDeletedUsers.concat(users)

          if (users.length < batchSize) {
            hasMore = false
          } else {
            skip += batchSize
          }
        } else {
          hasMore = false
        }
      }

      if (allDeletedUsers.length === 0) {
        showNotification({
          message: "No deleted users to remove.",
          className: "bg-admin_error text-white",
        })
        return
      }

      showPromptDialog({
        title: "Delete All Forever",
        message: `Are you sure you want to permanently delete all ${allDeletedUsers.length} user(s)? This action cannot be undone and there is no way to reverse it.`,
        confirmText: "Delete All Forever",
        onConfirm: async () => {
          try {
            // Delete all users - get all user IDs
            const userIds = allDeletedUsers.map(user => user._id)
            await UserPlatformAPI.HardDeleteUsers(userIds)

            showNotification({
              message: `Successfully deleted ${allDeletedUsers.length} user(s) permanently.`,
              className: "bg-admin_primary text-admin_text_inv",
            })

            // Force refresh the restore dialog table
            setTimeout(() => {
              setRefreshKey((prev) => prev + 1)
            }, 50)
            onRestore?.() // Refresh the main table
          } catch (error) {
            showNotification({
              message: "Failed to delete all users.",
              className: "bg-admin_error text-white",
            })
          }
        },
      })
    } catch (error) {
      showNotification({
        message: "Failed to fetch deleted users.",
        className: "bg-admin_error text-white",
      })
    }
  }

  const formatDate = (date) => {
    if (!date) return "N/A"
    try {
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return "Invalid date"
    }
  }

  const getOriginalEmail = (email) => {
    if (!email) return "No email"
    return email.includes('_deleted_') ? email.split('_deleted_')[0] : email
  }

  const getOriginalGoogleId = (googleId) => {
    if (!googleId) return "N/A"
    return googleId.includes('_deleted_') ? googleId.split('_deleted_')[0] : googleId
  }

  // Define columns for the data table
  const tableItemRender = [
    {
      id: "user",
      title: "User",
      headerClassName: "w-[200px]",
      render: (_, user) => (
        <div className="w-full flex items-center space-x-3">
          {/* User Avatar */}
          {renderAvatar(user, "w-10 h-10")}

          <div className="w-0 flex-1">
            <Typography 
              variant="admin_label" 
              className="truncate my-0"
            >
              {user.name || "Unknown User"}
            </Typography>

            <Tooltip 
              display="block"
              content={
                <div className="text-center">
                  <Typography 
                    variant="admin_txt1"
                    className="block"
                  >
                    {getOriginalEmail(user.email)}
                  </Typography>

                  <Typography variant="admin_txt2_dim">
                    Click to copy
                  </Typography>
                </div>
              }
            >
              <button 
                onClick={async () => {
                try {
                  await navigator.clipboard.writeText(getOriginalEmail(user.email))
                  showNotification({
                    message: "Copied to clipboard",
                    className: "bg-admin_primary text-white"
                  })
                } catch (error) {
                  console.error("Failed to copy:", error)
                }
              }}
                className="w-full truncate whitespace-nowrap text-admin_text_secondary hover:text-admin_primary transition-colors text-left"
              >
                {getOriginalEmail(user.email)}
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      id: "credentials",
      title: "Credentials",
      headerClassName: "w-[150px]",
      render: (_, user) => (
        <div className="text-sm">
          {user.google_id && (
            <div className="flex items-center gap-1 mb-1">
              <span className="text-admin_text_secondary">
                Google:
              </span>

              <Tooltip content={
                <div className="text-center">
                  <div>{getOriginalGoogleId(user.google_id)}</div>
                  <div className="text-xs text-admin_text_secondary mt-1">Click to copy</div>
                </div>
              }>
                <button 
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(getOriginalGoogleId(user.google_id))
                      showNotification({
                        message: "Copied to clipboard",
                        className: "bg-admin_primary text-admin_text_inv"
                      })
                    } catch (error) {
                      console.error("Failed to copy:", error)
                    }
                  }}
                  className="max-w-[80px] font-mono text-xs truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
                >
                  {getOriginalGoogleId(user.google_id)}
                </button>
              </Tooltip>
            </div>
          )}

          {user.apple_id && (
            <div className="flex items-center gap-1 mb-1">
              <span className="text-admin_text_secondary">
                Apple:
              </span>

              <Tooltip content={
                <div className="text-center">
                  <div>{user.apple_id.includes('_deleted_') ? user.apple_id.split('_deleted_')[0] : user.apple_id}</div>
                  <div className="text-xs text-admin_text_secondary mt-1">Click to copy</div>
                </div>
              }>
                <button 
                  onClick={async () => {
                    try {
                      const appleId = user.apple_id.includes('_deleted_') ? user.apple_id.split('_deleted_')[0] : user.apple_id
                      await navigator.clipboard.writeText(appleId)
                      showNotification({
                        message: "Copied to clipboard",
                        className: "bg-admin_primary text-admin_text_inv"
                      })
                    } catch (error) {
                      console.error("Failed to copy:", error)
                    }
                  }}
                  className="max-w-[80px] font-mono text-xs truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
                >
                  {user.apple_id.includes('_deleted_') ? user.apple_id.split('_deleted_')[0] : user.apple_id}
                </button>
              </Tooltip>
            </div>
          )}

          {user.facebook_id && (
            <div className="flex items-center gap-1">
              <span className="text-admin_text_secondary">
                Facebook:
              </span>

              <Tooltip content={
                <div className="text-center">
                  <div>{user.facebook_id.includes('_deleted_') ? user.facebook_id.split('_deleted_')[0] : user.facebook_id}</div>
                  <div className="text-xs text-admin_text_secondary mt-1">Click to copy</div>
                </div>
              }>
                <button 
                  onClick={async () => {
                    try {
                      const facebookId = user.facebook_id.includes('_deleted_') ? user.facebook_id.split('_deleted_')[0] : user.facebook_id
                      await navigator.clipboard.writeText(facebookId)
                      showNotification({
                        message: "Copied to clipboard",
                        className: "bg-admin_primary text-admin_text_inv"
                      })
                    } catch (error) {
                      console.error("Failed to copy:", error)
                    }
                  }}
                  className="max-w-[80px] font-mono text-xs truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
                >
                  {user.facebook_id.includes('_deleted_') ? user.facebook_id.split('_deleted_')[0] : user.facebook_id}
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      id: "devices",
      title: "Devices",
      headerClassName: "w-[100px] text-center",
      render: (_, user) => (
        <div className="flex items-center justify-center gap-1">
          <DevicePhoneMobileIcon className="w-4 h-4 text-admin_text_secondary" />
          <span className="text-admin_text">
            {user.devices?.length || 0}
          </span>
        </div>
      ),
    },
    {
      id: "created",
      title: "Created",
      headerClassName: "w-[100px]",
      render: (_, user) => (
        <Typography 
          variant="admin_txt1_dim"
          className="whitespace-nowrap"
        >
          {formatDate(user.created)}
        </Typography>
      ),
    },
    {
      id: "restore",
      title: "Restore",
      headerClassName: "w-[100px] text-center",
      render: (_, user) => (
        <Button
          variant="admin_secondary"
          className="w-20 mx-auto"
          onClick={() => restoreAction(user)}
        >
          Restore
        </Button>
      ),
    },
    {
      id: "delete",
      title: "Delete",
      headerClassName: "w-[120px] text-center",
      render: (_, user) => (
        <Button
          variant="admin_error"
          className="whitespace-nowrap mx-auto"
          onClick={() => deleteForeverAction(user)}
        >
          Delete Forever
        </Button>
      ),
    },
  ]

  const headerControls = (
    <div className="absolute top-[45px] right-[350px] xl:right-[410px] flex items-center gap-3">
      <Button
        variant="admin_error"
        onClick={deleteAllAction}
        className="whitespace-nowrap"
      >
        Delete All{deletedUsers.length > 0 ? ` (${deletedUsers.length})` : ''}
      </Button>
    </div>
  )

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      className="md:absolute w-full md:w-[90%] h-full md:h-100vh top-[15px]"
      zIndex={200}
    >
      <div className="p-4">
        <Typography variant="admin_h3" className="mb-4">
          Restore Deleted Users
        </Typography>

        {/* Data table for displaying deleted users */}
        <div className="h-[75vh]">
          <DataTable
            localUserPreferencesKey="admin.platformAccess.usersRestore"
            variant="admin"
            refreshKey={refreshKey}
            apiMethod={UserPlatformAPI.GetDeletedUsers}
            preferredHeight="75vh"
            itemHeight="h-[80px]"
            headerControls={headerControls}
            styleOverrides={{
              searchInput: "absolute top-[45px] right-[40px] w-full max-w-[300px] xl:max-w-[360px] ml-6 xl:ml-8"
            }}
            searchField="name email google_id apple_id facebook_id _id"
            searchObject={{}}
            sort={{ created: -1 }}
            renders={tableItemRender}
            onDataLoaded={(data) => {
              setDeletedUsers(data || [])
              return data
            }}
            noData={
              <div className="min-h-[400px] flex items-center justify-center">
                <Typography variant="admin_body" className="text-admin_text_secondary">
                  No deleted users found
                </Typography>
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  )
}

// User statistics overview component
const UserStatistics = ({ onRefresh }) => {
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    disabled: 0,
    deleted: 0
  })
  const [loading, setLoading] = useState(true)

  const loadStatistics = useCallback(async () => {
    setLoading(true)
    try {
      const response = await UserPlatformAPI.GetUserStatistics()
      if (response.result) {
        setStats(response.data)
      } else {
        console.error("Failed to load user statistics:", response.error)
      }
    } catch (error) {
      console.error("Failed to load user statistics:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatistics()
  }, [loadStatistics, onRefresh])

  const StatCard = ({ title, value, icon: Icon, variant = "primary", showPercentage = true }) => {
    const getColorClasses = () => {
      switch (variant) {
        case "success":
          return "bg-admin_success text-white"
        case "warning":
          return "bg-admin_warning text-white"
        case "error":
          return "bg-admin_error text-white"
        default:
          return "bg-admin_primary_lt text-white"
      }
    }

    // Calculate percentage based on total users (excluding deleted for active/disabled)
    const getPercentage = () => {
      if (loading || !showPercentage) return null
      
      let denominator = stats.total
      if (title === "Deleted Users") {
        // For deleted users, show percentage of all users including deleted
        denominator = stats.total + stats.deleted
      }
      
      if (denominator === 0) return "0%"
      
      const percentage = Math.round((value / denominator) * 100)
      return `${percentage}%`
    }

    return (
      <div className="bg-admin_paper border border-admin_bordered rounded-xl p-6 flex items-end justify-between">
        <div className="flex-1">
          <Typography 
            variant="admin_txt1_dim" 
            className="mb-2"
          >
            {title}
          </Typography>

          <div className="flex items-baseline gap-3">
            <Typography 
              component="output"
              variant="admin_h2" 
              className="text-admin_text font-bold"
            >
              {loading ? "..." : value.toLocaleString()}
            </Typography>

            {showPercentage && !loading && (
              <Typography variant="admin_txt1_dim">
                {getPercentage()}
              </Typography>
            )}
          </div>
        </div>

        <div className={`p-3 rounded-lg ${getColorClasses()}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <StatCard
        title="Total Users"
        value={stats.total}
        icon={UsersIcon}
        variant="primary"
        showPercentage={false}
      />
      <StatCard
        title="Active Users"
        value={stats.active}
        icon={UserGroupIcon}
        variant="success"
      />
      <StatCard
        title="Disabled Users"
        value={stats.disabled}
        icon={ExclamationTriangleIcon}
        variant="warning"
      />
      <StatCard
        title="Deleted Users"
        value={stats.deleted}
        icon={TrashIcon}
        variant="error"
      />
    </div>
  )
}

const PlatformUsers = () => {
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedUserData, setSelectedUserData] = useState([])
  const [userDetailsModal, setUserDetailsModal] = useState({ isOpen: false, user: null })
  const [devicesModal, setDevicesModal] = useState({ isOpen: false, user: null })
  const [credentialsModal, setCredentialsModal] = useState({ isOpen: false, user: null })
  const [restoreModal, setRestoreModal] = useState(false)
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, userCount: 0, isBulk: false, onConfirm: null })
  const [createUserModal, setCreateUserModal] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState([{ value: "all", label: "All Status" }])
  const [credentialsFilter, setCredentialsFilter] = useState([{ value: "all", label: "All Credentials" }])
  const [dateFilter, setDateFilter] = useState({ type: "preset", value: null }) // CMS-style date filter

  // Build request fields based on filters
  const getRequestFields = () => {
    const match = {
      deleted: { $ne: true }
    }

    // Status filter
    const selectedStatus = statusFilter[0]?.value
    if (selectedStatus && selectedStatus !== "all") {
      match.enabled = selectedStatus === "active"
    }

    // Credentials filter
    const selectedCredentials = credentialsFilter[0]?.value
    if (selectedCredentials && selectedCredentials !== "all") {
      switch (selectedCredentials) {
        case "google":
          match.google_id = { $exists: true, $ne: null }
          break
        case "apple":
          match.apple_id = { $exists: true, $ne: null }
          break
        case "facebook":
          match.facebook_id = { $exists: true, $ne: null }
          break
        case "internal":
          match.internal_id = { $exists: true, $ne: null }
          break
        case "stripe":
          match.stripe_customer_id = { $exists: true, $ne: null }
          break
        case "none":
          match.$and = [
            { google_id: { $exists: false } },
            { apple_id: { $exists: false } },
            { facebook_id: { $exists: false } },
            { internal_id: { $exists: false } },
            { stripe_customer_id: { $exists: false } }
          ]
          break
      }
    }

    // Date filter (CMS-style)
    if (dateFilter.type === "preset" && dateFilter.value) {
      // Preset date range (e.g., last 7 days, last 30 days)
      const cutoffDate = moment().subtract(dateFilter.value.days, "days").utc().toISOString()
      match.created = { $gte: cutoffDate }
    } else if (dateFilter.type === "custom" && dateFilter.range) {
      // Custom date range
      const startDate = moment(dateFilter.range.startDate).utc().startOf("day").toISOString()
      const endDate = moment(dateFilter.range.endDate).utc().endOf("day").toISOString()
      match.created = { $gte: startDate, $lte: endDate }
    }

    return { match }
  }

  const handleSelectUser = (user, isSelected) => {
    if (isSelected) {
      setSelectedUsers([...selectedUsers, user._id])
      setSelectedUserData([...selectedUserData, user])
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== user._id))
      setSelectedUserData(selectedUserData.filter(u => u._id !== user._id))
    }
  }

  const handleSelectAll = (users, isSelected) => {
    if (isSelected) {
      setSelectedUsers(users.map(u => u._id))
      setSelectedUserData([...users])
    } else {
      setSelectedUsers([])
      setSelectedUserData([])
    }
  }

  const handleDeleteSelected = () => {
    if (selectedUsers.length === 0) return

    setDeleteConfirmModal({
      isOpen: true,
      userCount: selectedUsers.length,
      isBulk: true,
      onConfirm: async () => {
        try {
          const response = await UserPlatformAPI.DeleteUsers(selectedUsers)
          if (response.result) {
            setSelectedUsers([])
            setSelectedUserData([])
            setRefreshKey(prev => prev + 1)
          }
        } catch (error) {
          console.error("Failed to delete users:", error)
        }
        setDeleteConfirmModal({ isOpen: false, userCount: 0, isBulk: false, onConfirm: null })
      }
    })
  }

  const handleDeselectAll = () => {
    setSelectedUsers([])
    setSelectedUserData([])
  }

  const handleViewUser = (user) => {
    setUserDetailsModal({ isOpen: true, user })
  }

  const handleViewDevices = (user) => {
    setDevicesModal({ isOpen: true, user })
  }

  const handleViewCredentials = (user) => {
    setCredentialsModal({ isOpen: true, user })
  }

  const handleToggleUserStatus = async (user) => {
    try {
      const response = await UserPlatformAPI.ToggleUserStatus(user._id, !user.enabled)
      if (response.result) {
        setRefreshKey(prev => prev + 1)
        setUserDetailsModal({ isOpen: false, user: null })
      }
    } catch (error) {
      console.error("Failed to toggle user status:", error)
    }
  }

  const handleDeleteUser = (user) => {
    setDeleteConfirmModal({
      isOpen: true,
      userCount: 1,
      isBulk: false,
      onConfirm: async () => {
        try {
          const response = await UserPlatformAPI.DeleteUsers([user._id])
          if (response.result) {
            setRefreshKey(prev => prev + 1)
            setUserDetailsModal({ isOpen: false, user: null })
          }
        } catch (error) {
          console.error("Failed to delete user:", error)
        }
        setDeleteConfirmModal({ isOpen: false, userCount: 0, isBulk: false, onConfirm: null })
      }
    })
  }

  const handleRestoreUsers = async () => {
    // The restore dialog now handles individual restore actions
    // Just refresh the main table when restore dialog actions occur
    setRefreshKey(prev => prev + 1)
  }

  // Filter handlers
  const handleStatusFilterChange = (values) => {
    setStatusFilter(values?.length ? values : [{ value: "all", label: "All Status" }])
    setRefreshKey(prev => prev + 1)
  }

  const handleCredentialsFilterChange = (values) => {
    setCredentialsFilter(values?.length ? values : [{ value: "all", label: "All Credentials" }])
    setRefreshKey(prev => prev + 1)
  }

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter)
    setRefreshKey(prev => prev + 1)
  }

  // Header controls
  const headerControls = (
    <div className="w-full flex items-end gap-4">
      {/* Status Filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim" 
          className="mb-0.5"
        >
          Status
        </Typography>

        <DropdownSelect
          variant="admin"
          options={[
            { value: "all", label: "All Status" },
            { value: "active", label: "Active" },
            { value: "disabled", label: "Disabled" }
          ]}
          value={statusFilter}
          onChange={handleStatusFilterChange}
          fixedWidth="120px"
          showCheckbox={false}
        />
      </div>

      {/* Credentials Filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim" 
          className="mb-0.5"
        >
          Credentials
        </Typography>

        <DropdownSelect
          variant="admin"
          options={[
            { value: "all", label: "All Credentials" },
            { value: "google", label: "Google" },
            { value: "apple", label: "Apple" },
            { value: "facebook", label: "Facebook" },
            { value: "internal", label: "Internal" },
            { value: "stripe", label: "Stripe" },
            { value: "none", label: "No Credentials" }
          ]}
          value={credentialsFilter}
          onChange={handleCredentialsFilterChange}
          fixedWidth="150px"
          showCheckbox={false}
        />
      </div>

      {/* Date Filter */}
      <div className="flex flex-col">
        <DateFilterDropdown
          variant="admin"
          value={dateFilter}
          onChange={handleDateFilterChange}
          className="w-[190px]"
          styleOverrides={{
            label: "text-admin_text_secondary text-xs font-normal mb-0.5",
          }}
        />
      </div>
    </div>
  )

  return (
    <div className="p-6">
      <UserStatistics onRefresh={refreshKey} />

      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          Platform Users
        </Typography>

        <div className="flex items-end justify-end gap-3">
          <Button 
            variant="admin_secondary" 
            onClick={() => setRestoreModal(true)}
          >
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            Restore
          </Button>

          <Button
            variant="admin_primary"
            onClick={() => setCreateUserModal(true)}
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>
      
      <DataTable
        localUserPreferencesKey="admin.platformAccess.usersBrowser"
        variant="admin_browser"
        apiMethod={UserPlatformAPI.GetUsers}
        requestFields={getRequestFields()}
        headerControls={headerControls}
        enableSelection={true}
        selectedItems={selectedUsers}
        selectedData={selectedUserData}
        onSelectionChange={(ids, data) => {
          setSelectedUsers(ids);
          setSelectedUserData(data);
        }}
        bulkActions={[
          {
            label: "Delete",
            variant: "admin_error",
            icon: <TrashIcon className="w-4 h-4" />,
            onClick: handleDeleteSelected,
          }
        ]}
        itemIdField="_id"
        searchField="name email google_id apple_id facebook_id internal_id stripe_customer_id _id"
        itemsPerPage={25}
        refreshKey={refreshKey}
        preferredHeight="70vh"
        styleOverrides={{
          table: "w-full table-fixed",
          header: "w-full flex justify-between items-end gap-2 -mt-5 mb-4",
          container: "relative w-full flex flex-col h-[72vh] justify-start bg-admin_paper rounded-3xl px-6 pt-8 pb-6",
          searchInput: "absolute w-[350px] h-[30px] top-6 right-4 text-text bg-transparent outline-none flex-1 px-2 py-1",
        }}
        renders={[
          {
            id: "user",
            title: "User",
            headerClassName: "w-[30%]",
            render: (_, user) => (
              <button
                className="w-full flex items-center space-x-3 text-left"
                onClick={() => handleViewUser(user)}
              >
                {renderAvatar(user, "w-10 h-10")}

                <div className="w-0 flex-1">
                  <Typography
                    variant="admin_body"
                    className="font-medium truncate my-0"
                    data-user-name={user._id}
                  >
                    {user.name || "Unknown User"}
                  </Typography>

                  {user.email && (
                    <Typography
                      variant="admin_txt1_dim"
                      className="block truncate my-0"
                    >
                      {user.email}
                    </Typography>
                  )}
                </div>
              </button>
            ),
          },
          {
            id: "mongoId",
            title: "ID",
            headerClassName: "w-[15%]",
            render: (_, user) => (
              <Tooltip 
                display="block"
                content={
                  <div className="text-center">
                    <Typography 
                      variant="admin_txt1"
                      className="block"
                    >
                      {user._id}
                    </Typography>
                    <Typography variant="admin_txt2_dim">
                      Click to copy
                    </Typography>
                  </div>
                }
              >
                <button 
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(user._id)
                      showNotification({
                        message: "MongoDB ID copied to clipboard",
                        className: "bg-admin_primary text-admin_text_inv"
                      })
                    } catch (error) {
                      showNotification({
                        message: "Failed to copy MongoDB ID",
                        className: "bg-admin_error text-white"
                      })
                    }
                  }}
                  className="w-full text-left truncate hover:text-admin_primary transition-colors"
                >
                  <Typography 
                    variant="admin_txt1" 
                    className="truncate text-admin_text_secondary hover:text-admin_primary"
                  >
                    {user._id}
                  </Typography>
                </button>
              </Tooltip>
            ),
          },
          {
            id: "credentials",
            title: "Credentials",
            headerClassName: "w-[12%]",
            render: (_, user) => <UserCredentials user={user} onViewCredentials={handleViewCredentials} />,
          },
          {
            id: "devices",
            title: "Devices",
            headerClassName: "w-[10%]",
            render: (_, user) => <UserDevices user={user} onViewDevices={handleViewDevices} />,
          },
          {
            id: "status",
            title: "Status",
            headerClassName: "w-[10%]",
            render: (_, user) => (
              <Badge variant={user.enabled ? "admin_success" : "admin_error"}>
                {user.enabled ? "Active" : "Disabled"}
              </Badge>
            ),
          },
          {
            id: "created",
            title: "Created",
            headerClassName: "w-[10%]",
            render: (_, user) => (
              <Typography variant="admin_txt1" className="block text-admin_text_secondary">
                {new Date(user.created).toLocaleDateString()}
              </Typography>
            ),
          },
          {
            id: "actions",
            title: "Actions",
            headerClassName: "w-[10%] text-center",
            render: (_, user) => (
              <div className="flex items-center justify-center">
                <button
                  className="p-1.5 rounded hover:bg-admin_neutral_1/50 text-admin_text_secondary hover:text-admin_primary transition-colors"
                  onClick={() => handleViewUser(user)}
                  title="View user details"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
              </div>
            ),
          },
        ]}
        noData={
          <div className="text-center py-12">
            <UsersIcon className="w-16 h-16 text-admin_text_secondary mx-auto mb-4" />

            <Typography 
              variant="admin_h4" 
              className="text-admin_text_secondary mb-2"
            >
              No users found
            </Typography>
            
            <Typography variant="admin_txt1_dim">
              Start by adding your first platform user
            </Typography>
          </div>
        }
      />

      {/* Modals */}
      <UserInfoDrawer
        userObject={userDetailsModal.user}
        isOpen={userDetailsModal.isOpen}
        onClose={() => setUserDetailsModal({ isOpen: false, user: null })}
        onDelete={(user) => {
          setRefreshKey(prev => prev + 1)
          setUserDetailsModal({ isOpen: false, user: null })
        }}
        onStatusChange={(user, enabled) => {
          setRefreshKey(prev => prev + 1)
        }}
      />

      <DevicesModal
        user={devicesModal.user}
        isOpen={devicesModal.isOpen}
        onClose={() => setDevicesModal({ isOpen: false, user: null })}
      />

      <UserCredentialsModal
        user={credentialsModal.user}
        isOpen={credentialsModal.isOpen}
        onClose={() => setCredentialsModal({ isOpen: false, user: null })}
      />

      <RestoreUsersModal
        isOpen={restoreModal}
        onClose={() => setRestoreModal(false)}
        onRestore={handleRestoreUsers}
      />

      <DeleteConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, userCount: 0, isBulk: false, onConfirm: null })}
        onConfirm={deleteConfirmModal.onConfirm}
        userCount={deleteConfirmModal.userCount}
        isBulk={deleteConfirmModal.isBulk}
      />

      <CreateUserModal
        isOpen={createUserModal}
        onClose={() => setCreateUserModal(false)}
        onSuccess={() => {
          // Refresh the data table when a new user is created
          setRefreshKey(prev => prev + 1)
        }}
      />
    </div>
  )
}

export default PlatformUsers